import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { SEVERITY, fmtTimeTz } from "@/lib/fleet";
import { AddressLabel } from "@/components/AddressLabel";
import { RefreshCw, Check, X, AlertCircle, ShieldCheck, Loader2, Gauge, ChevronRight, ChevronDown, SlidersHorizontal, MapPin, Route, Clock } from "lucide-react";

// A10 — Détection « hors horaires » (déplacement en dehors des heures autorisées).
// Règle composée avec une condition d'horaire (time_window/weekday), ou libellé explicite.
const OFF_HOURS_RE = /hors horaires|dehors des heures|hors des heures/i;
export const isOffHoursEpisode = (ep) => {
  if (!ep) return false;
  const txt = `${ep.message || ""} ${ep.rule_name || ""} ${ep.name || ""}`;
  if (OFF_HOURS_RE.test(txt)) return true;
  if (ep.type === "composee" && Array.isArray(ep.conditions_snapshot)) {
    return ep.conditions_snapshot.some((g) => (g.conditions || []).some((c) => ["time_window", "weekday"].includes(c.metric)));
  }
  return false;
};

export const EP_TYPES = {
  exces_vitesse: "Excès de vitesse",
  ralenti: "Marche au ralenti",
  arret_prolonge: "Arrêt prolongé",
  batterie: "Batterie faible",
  reseau: "Perte réseau",
  deconnexion: "Déconnexion",
  composee: "Alerte composée",
};
const CLOSE_REASONS = {
  condition_cleared: "Condition rétablie",
  rule_updated: "Règle modifiée",
  rule_disabled: "Règle désactivée",
  rule_deleted: "Règle supprimée",
};
const CMETRIC = { speed: "Vitesse", ignition: "Contact", stopped: "À l'arrêt", idling: "Ralenti",
  battery: "Batterie", gsm: "Signal GSM", zone: "Zone", poi: "POI", time_window: "Plage horaire", weekday: "Jours" };
const condText = (c) => {
  const m = c.metric;
  if (m === "battery") return `Batterie ${c.op} ${c.value != null && c.value !== "" ? (c.value / 1000).toFixed(1) + " V" : "?"}`;
  if (m === "speed") return `Vitesse ${c.op} ${c.value || "?"} km/h`;
  if (m === "gsm") return `Signal GSM ${c.op} ${c.value ?? "?"}`;
  if (m === "ignition") return `Contact ${c.value === "on" ? "actif" : "coupé"}`;
  if (m === "stopped") return "Véhicule à l'arrêt";
  if (m === "idling") return "Moteur au ralenti";
  if (m === "zone") return `${c.op === "outside" ? "Hors de" : "Dans"} la zone`;
  if (m === "poi") return `${c.op === "outside" ? "Hors du" : "Dans le"} POI`;
  if (m === "time_window") return `Entre ${c.value?.start || "?"} et ${c.value?.end || "?"}`;
  if (m === "weekday") return `Jours ${(c.value || []).join(",")}`;
  return CMETRIC[m] || m;
};
const EP_DEFAULTS = { arret_prolonge: 900, ralenti: 300, batterie_open: 11000, batterie_close: 11500, offline: 300 };

export const fmtDur = (s) => {
  if (s == null) return "—";
  s = Math.max(0, Math.round(s));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (h) return `${h}h${String(m).padStart(2, "0")}`;
  if (m) return `${m} min`;
  return `${sec}s`;
};
const nowIso = () => new Date().toISOString();
const daysAgoIso = (d) => new Date(Date.now() - d * 86400000).toISOString();

export const valSummary = (ep) => {
  switch (ep.type) {
    case "batterie":
      return ep.min_value != null ? `min ${(ep.min_value / 1000).toFixed(1)} V / ${(((ep.threshold ?? EP_DEFAULTS.batterie_open)) / 1000).toFixed(1)} V` : "—";
    case "exces_vitesse":
      return ep.max_value != null ? `${Math.round(ep.max_value)} / ${Math.round(ep.threshold ?? 0)} km/h` : "—";
    case "arret_prolonge":
    case "ralenti":
      return fmtDur(ep.duration_s);
    case "deconnexion":
      return fmtDur(ep.duration_s);
    case "composee":
      return ep.rule_name || "règle";
    default:
      return "—";
  }
};

export default function AlertEpisodesView({ active, defaultStatus, historyMode }) {
  const { user } = useAuth();
  const canAck = user && ["admin", "gestionnaire"].includes(user.role);

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [detail, setDetail] = useState(null);

  // filtres
  const [statusTab, setStatusTab] = useState(defaultStatus || "open"); // open | closed | all
  const [period, setPeriod] = useState("7"); // 1 | 7 | 30 | custom
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [fCompany, setFCompany] = useState("");
  const [fPool, setFPool] = useState("");
  const [fVehicle, setFVehicle] = useState("");
  const [fType, setFType] = useState("");
  const [fSeverity, setFSeverity] = useState("");
  const [fAck, setFAck] = useState("all"); // all | true | false
  const [secondaryOpen, setSecondaryOpen] = useState(false); // historyMode : section repliable (compteurs + filtres secondaires)

  const [companies, setCompanies] = useState([]);
  const [pools, setPools] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const LIMIT = 50;
  const detailRef = useRef(null);
  detailRef.current = detail;

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => {});
    api.get("/pools").then((r) => setPools(r.data || [])).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
  }, []);

  // Deep-link A6/A5 : ?episode=<id> -> charge l'épisode par id (même hors page/période/filtres) et ouvre le drawer
  useEffect(() => {
    const eid = new URLSearchParams(window.location.search).get("episode");
    if (!eid) return;
    api.get(`/alert-episodes/${eid}`).then((r) => setDetail(r.data)).catch(() => {
      // A8 : l'épisode a pu expirer (TTL) ou être hors scope -> 404 neutre. Toast + retour propre à la vue.
      toast.error("Épisode expiré ou introuvable");
      const u = new URL(window.location.href);
      u.searchParams.delete("episode");
      window.history.replaceState({}, "", u);
    });
  }, []);

  const periodParams = useCallback(() => {
    if (period === "custom") {
      const p = {};
      if (customFrom) p.date_from = new Date(customFrom + "T00:00:00").toISOString();
      if (customTo) p.date_to = new Date(customTo + "T23:59:59").toISOString();
      return p;
    }
    return { date_from: daysAgoIso(Number(period)), date_to: nowIso() };
  }, [period, customFrom, customTo]);

  const commonParams = useCallback(() => {
    const p = { ...periodParams() };
    if (statusTab !== "all") p.status = statusTab;
    if (fCompany) p.company_id = fCompany;
    if (fPool) p.pool_id = fPool;
    if (fVehicle) p.vehicle_id = fVehicle;
    if (fType) p.type = fType;
    if (fSeverity) p.severity = fSeverity;
    if (fAck !== "all") p.acknowledged = fAck;
    return p;
  }, [periodParams, statusTab, fCompany, fPool, fVehicle, fType, fSeverity, fAck]);

  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get(`/alert-episodes/summary?${new URLSearchParams(commonParams())}`);
      setSummary(r.data);
    } catch { /* silencieux : KPI non bloquant */ }
  }, [commonParams]);

  const loadList = useCallback(async (nextSkip = 0, append = false) => {
    if (!append) setLoading(true);
    setError(false);
    try {
      const p = { ...commonParams(), limit: String(LIMIT), skip: String(nextSkip) };
      const r = await api.get(`/alert-episodes?${new URLSearchParams(p)}`);
      const data = r.data || [];
      setItems((prev) => (append ? [...prev, ...data] : data));
      setHasMore(data.length === LIMIT);
      setSkip(nextSkip);
    } catch {
      setError(true);
      if (!append) setItems([]);
    } finally {
      setLoading(false);
    }
  }, [commonParams]);

  // rechargement sur changement de filtres
  useEffect(() => { loadList(0, false); loadSummary(); /* eslint-disable-next-line */ }, [commonParams]);

  // auto-refresh 60s : uniquement onglet actif + visible ; ne ferme jamais le drawer
  useEffect(() => {
    if (!active) return;
    const tick = () => {
      if (document.hidden) return;
      loadSummary();
      if (!detailRef.current) loadList(0, false); // drawer ouvert -> on ne recharge que le summary
    };
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [active, loadSummary, loadList]);

  const refresh = () => { loadList(0, false); loadSummary(); };

  const ack = async (ep, e) => {
    e?.stopPropagation();
    try {
      const r = await api.post(`/alert-episodes/${ep.id}/ack`);
      const upd = r.data;
      setItems((prev) => prev.map((x) => (x.id === ep.id ? { ...x, ...upd } : x)));
      if (detail?.id === ep.id) setDetail((d) => ({ ...d, ...upd }));
      toast.success("Épisode acquitté");
      loadSummary();
    } catch (err) {
      toast.error(err.response?.status === 403 ? "Acquittement non autorisé" : "Erreur");
    }
  };

  const KPIS = [
    { key: "open", label: "Ouverts", color: "#17A2B8" },
    { key: "unacked", label: "Non acquittés", color: "#F59E0B" },
    { key: "critical", label: "Critiques", color: "#EF4444" },
    { key: "new", label: "Nouveaux", color: "#8B5CF6" },
    { key: "vehicles", label: "Véhicules", color: "#10B981" },
  ];

  const kpiGrid = (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="episode-kpis">
      {KPIS.map((k) => (
        <div key={k.key} className="border border-border rounded-lg bg-card px-4 py-3" data-testid={`kpi-${k.key}`}>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">{k.label}</div>
          <div className="text-2xl font-extrabold" style={{ color: k.color }}>{summary ? summary[k.key] : "—"}</div>
        </div>
      ))}
    </div>
  );

  const severityAckFilters = (
    <>
      <select value={fSeverity} onChange={(e) => setFSeverity(e.target.value)} data-testid="filter-severity" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs">
        <option value="">Toutes sévérités</option>
        <option value="high">Critique</option>
        <option value="medium">Moyen</option>
        <option value="low">Info</option>
      </select>
      <select value={fAck} onChange={(e) => setFAck(e.target.value)} data-testid="filter-ack" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs">
        <option value="all">Acquittés + non</option>
        <option value="false">Non acquittés</option>
        <option value="true">Acquittés</option>
      </select>
    </>
  );

  return (
    <div data-testid="alert-episodes-view">
      {/* KPI — masqués en mode Historique (déplacés dans la section repliable) */}
      {!historyMode && <div className="mb-4">{kpiGrid}</div>}

      {/* Filtres */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {!historyMode && (
          <div className="flex rounded-md border border-border overflow-hidden" data-testid="status-tabs">
            {[["open", "Ouvertes"], ["closed", "Historique"], ["all", "Toutes"]].map(([v, l]) => (
              <button key={v} onClick={() => setStatusTab(v)} data-testid={`status-${v}`}
                className={`px-3 py-2 text-xs font-semibold ${statusTab === v ? "bg-[#17A2B8] text-white" : "bg-card text-muted-foreground hover:bg-black/5"}`}>{l}</button>
            ))}
          </div>
        )}
        <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="period-select" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs outline-none">
          <option value="1">24 h</option>
          <option value="7">7 jours</option>
          <option value="30">30 jours</option>
          <option value="custom">Personnalisée</option>
        </select>
        {period === "custom" && (
          <>
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="period-from" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs" />
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="period-to" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs" />
          </>
        )}
        {companies.length > 1 && (
          <select value={fCompany} onChange={(e) => setFCompany(e.target.value)} data-testid="filter-company" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs">
            <option value="">Toutes sociétés</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <select value={fPool} onChange={(e) => setFPool(e.target.value)} data-testid="filter-pool" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs">
          <option value="">Tous les groupes</option>
          {pools.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={fVehicle} onChange={(e) => setFVehicle(e.target.value)} data-testid="filter-vehicle" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs">
          <option value="">Tous véhicules</option>
          {vehicles.map((v) => <option key={v.id} value={v.id}>{[v.name, v.plate].filter(Boolean).join(" · ") || v.imei || v.id}</option>)}
        </select>
        <select value={fType} onChange={(e) => setFType(e.target.value)} data-testid="filter-type" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs">
          <option value="">Tous types</option>
          {Object.entries(EP_TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {!historyMode && severityAckFilters}
        <button onClick={refresh} data-testid="refresh-btn" className="flex items-center gap-1.5 border border-border hover:border-[#17A2B8] hover:text-[#17A2B8] text-muted-foreground font-semibold px-3 py-2 rounded-md text-xs transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Actualiser
        </button>
      </div>

      {/* Mode Historique : compteurs + filtres secondaires dans une section repliable */}
      {historyMode && (
        <div className="mb-4">
          <button onClick={() => setSecondaryOpen((o) => !o)} data-testid="history-more-toggle"
            className="flex items-center gap-1.5 border border-border hover:border-[#17A2B8] hover:text-[#17A2B8] text-muted-foreground font-semibold px-3 py-2 rounded-md text-xs transition-colors">
            <SlidersHorizontal className="w-3.5 h-3.5" /> Compteurs et filtres
            {secondaryOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          {secondaryOpen && (
            <div className="mt-3 space-y-3 border border-border rounded-lg bg-secondary/30 p-3" data-testid="history-secondary">
              {kpiGrid}
              <div className="flex items-center gap-2 flex-wrap">{severityAckFilters}</div>
            </div>
          )}
        </div>
      )}

      {/* Liste */}
      <div className="border border-border rounded-lg overflow-hidden bg-card">
        {loading ? (
          <div className="flex justify-center py-14" data-testid="episodes-loading"><Loader2 className="w-6 h-6 animate-spin text-[#17A2B8]" /></div>
        ) : error ? (
          <div className="text-center py-12 px-6 text-sm text-[#EF4444] flex flex-col items-center gap-2" data-testid="episodes-error">
            <AlertCircle className="w-8 h-8" /> Impossible de charger les épisodes.
            <button onClick={refresh} className="mt-2 text-[#17A2B8] hover:underline">Réessayer</button>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-14 px-6 text-sm text-muted-foreground flex flex-col items-center gap-2" data-testid="episodes-empty">
            <ShieldCheck className="w-9 h-9 text-[#10B981]/70" />
            {historyMode ? "Aucune alerte clôturée sur cette période." : statusTab === "open" ? "Aucune alerte ouverte. Tout est nominal." : "Aucun résultat pour ces filtres."}
          </div>
        ) : (
          <>
            <table className="w-full text-sm hidden lg:table" data-testid="episodes-table">
              <thead className="bg-secondary/40"><tr className="label-mono text-left">
                <th className="px-3 py-3">Statut</th><th className="px-3 py-3">Sévérité</th><th className="px-3 py-3">Type</th>
                <th className="px-3 py-3">Véhicule</th><th className="px-3 py-3">Début</th><th className="px-3 py-3">Durée</th>
                <th className="px-3 py-3">Valeur / seuil</th><th className="px-3 py-3">ACK</th>
              </tr></thead>
              <tbody className="data">
                {items.map((ep) => {
                  const sev = SEVERITY[ep.severity] || SEVERITY.low;
                  return (
                    <tr key={ep.id} onClick={() => setDetail(ep)} data-testid={`episode-row-${ep.id}`} className="border-t border-border hover:bg-black/5 cursor-pointer">
                      <td className="px-3 py-2.5"><span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${ep.status === "open" ? "text-[#17A2B8] bg-[#17A2B8]/15" : "text-muted-foreground bg-muted"}`}>{ep.status === "open" ? "Ouvert" : "Fermé"}</span></td>
                      <td className="px-3 py-2.5"><span className="px-2 py-0.5 rounded text-[11px] font-bold" style={{ color: sev.color, background: `${sev.color}22`, border: `1px solid ${sev.color}55` }}>{sev.label}</span></td>
                      <td className="px-3 py-2.5 font-semibold">{EP_TYPES[ep.type] || ep.type}</td>
                      <td className="px-3 py-2.5">{vehLabel(ep)}</td>
                      <td className="px-3 py-2.5 text-muted-foreground text-xs">{fmtTimeTz(ep.started_at, ep.company_timezone)}</td>
                      <td className="px-3 py-2.5 text-xs">{ep.status === "open" ? "en cours" : fmtDur(ep.duration_s)}</td>
                      <td className="px-3 py-2.5 text-xs">{valSummary(ep)}</td>
                      <td className="px-3 py-2.5">
                        {ep.acknowledged ? <span className="text-[10px] text-muted-foreground">acquitté</span>
                          : canAck ? <button onClick={(e) => ack(ep, e)} data-testid={`ack-episode-${ep.id}`} className="text-[#17A2B8] hover:text-foreground p-1.5 rounded hover:bg-[#17A2B8]/20"><Check className="w-4 h-4" /></button>
                            : <span className="text-[10px] text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile : cartes condensées */}
            <div className="lg:hidden divide-y divide-border" data-testid="episodes-cards">
              {items.map((ep) => {
                const sev = SEVERITY[ep.severity] || SEVERITY.low;
                return (
                  <div key={ep.id} onClick={() => setDetail(ep)} data-testid={`episode-card-${ep.id}`} className="px-4 py-3 flex items-center gap-3 cursor-pointer active:bg-black/5">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ color: sev.color, background: `${sev.color}22` }}>{sev.label}</span>
                        <span className="font-semibold text-sm truncate">{EP_TYPES[ep.type] || ep.type}</span>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{vehLabel(ep)} · {fmtTimeTz(ep.started_at, ep.company_timezone)}</div>
                    </div>
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full shrink-0 ${ep.status === "open" ? "text-[#17A2B8] bg-[#17A2B8]/15" : "text-muted-foreground bg-muted"}`}>{ep.status === "open" ? "Ouvert" : "Fermé"}</span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <div className="p-3 text-center border-t border-border">
                <button onClick={() => loadList(skip + LIMIT, true)} data-testid="load-more" className="text-sm text-[#17A2B8] hover:underline font-semibold">Charger plus</button>
              </div>
            )}
          </>
        )}
      </div>

      {detail && <EpisodeDrawer ep={detail} onClose={() => setDetail(null)} canAck={canAck} onAck={(e) => ack(detail, e)} />}
    </div>
  );
}

const vehLabel = (ep) => [ep.vehicle || ep.vehicle_name, ep.plate].filter(Boolean).join(" · ") || "—";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-border/50 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-right">{value ?? "—"}</span>
  </div>
);

// A10 — Liens vérifiés pour un épisode « hors horaires » : position mesurée au déclenchement + trajet concerné.
// Prévalide via l'endpoint protégé pour n'afficher le lien « trajet » que si un trajet réel existe,
// et pour signaler si la position mesurée est indisponible dans la borne. Aucune coordonnée/token/timestamp
// privé n'est mis dans l'URL : seul l'id d'épisode + un focus (trigger|trip).
function OffHoursLinks({ ep }) {
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error
  useEffect(() => {
    let cancel = false;
    setState("loading");
    api.get(`/alert-episodes/${encodeURIComponent(ep.id)}/trigger-context`)
      .then((r) => { if (!cancel) { setCtx(r.data); setState("ready"); } })
      .catch(() => { if (!cancel) setState("error"); });
    return () => { cancel = true; };
  }, [ep.id]);

  const go = (focus) => navigate(`/historique?episode=${encodeURIComponent(ep.id)}&focus=${focus}`);

  return (
    <div className="pt-3 mt-2" data-testid="off-hours-links">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold mb-1.5">Déplacement hors horaires</p>
      {state === "loading" && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="off-hours-loading">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Vérification des données historiques…
        </div>
      )}
      {state === "error" && (
        <p className="text-xs text-[#EF4444]" data-testid="off-hours-error">Contexte indisponible pour le moment.</p>
      )}
      {state === "ready" && ctx && (
        <div className="space-y-2">
          <button onClick={() => go("trigger")} data-testid="off-hours-trigger-link"
            className="w-full flex items-center justify-between gap-2 border border-border hover:border-[#17A2B8] hover:text-[#17A2B8] rounded-md px-3 py-2 text-xs font-semibold transition-colors">
            <span className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Voir la position au déclenchement</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          {!ctx.trigger_point && (
            <p className="text-[10px] text-muted-foreground flex items-center gap-1" data-testid="off-hours-trigger-note">
              <Clock className="w-3 h-3" /> Position historique indisponible dans la borne de ±{Math.round((ctx.max_offset_seconds || 300) / 60)} min.
            </p>
          )}
          {ctx.trip_available ? (
            <button onClick={() => go("trip")} data-testid="off-hours-trip-link"
              className="w-full flex items-center justify-between gap-2 border border-border hover:border-[#17A2B8] hover:text-[#17A2B8] rounded-md px-3 py-2 text-xs font-semibold transition-colors">
              <span className="flex items-center gap-2"><Route className="w-3.5 h-3.5" /> Voir le trajet concerné</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <p className="text-[10px] text-muted-foreground flex items-center gap-1" data-testid="off-hours-trip-note">
              <Route className="w-3 h-3" /> Aucun trajet historique disponible autour du déclenchement.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function EpisodeDrawer({ ep, onClose, canAck, onAck }) {
  const sev = SEVERITY[ep.severity] || SEVERITY.low;
  const tz = ep.company_timezone;
  const typeRows = () => {
    switch (ep.type) {
      case "batterie":
        return [
          ["Minimum observé", ep.min_value != null ? `${(ep.min_value / 1000).toFixed(2)} V` : "—"],
          ["Seuil d'alerte", ep.threshold != null ? `${(ep.threshold / 1000).toFixed(2)} V` : `${(EP_DEFAULTS.batterie_open / 1000).toFixed(1)} V (défaut)`],
          ["Seuil de rétablissement", ep.rearm_threshold != null ? `${(ep.rearm_threshold / 1000).toFixed(2)} V` : `${(EP_DEFAULTS.batterie_close / 1000).toFixed(1)} V (défaut)`],
        ];
      case "exces_vitesse":
        return [
          ["Vitesse maximale", ep.max_value != null ? `${Math.round(ep.max_value)} km/h` : "—"],
          ["Limite", ep.threshold != null ? `${Math.round(ep.threshold)} km/h` : "—"],
          ["Dépassement max", (ep.max_value != null && ep.threshold != null) ? `${Math.round(ep.max_value - ep.threshold)} km/h` : "—"],
        ];
      case "arret_prolonge":
      case "ralenti":
        return [
          ["Durée minimale configurée", fmtDur(ep.min_duration_s ?? (ep.type === "arret_prolonge" ? EP_DEFAULTS.arret_prolonge : EP_DEFAULTS.ralenti)) + (ep.min_duration_s == null ? " (défaut)" : "")],
          ["Durée réelle", ep.status === "open" ? "en cours" : fmtDur(ep.duration_s)],
        ];
      case "deconnexion":
        return [
          ["Seuil hors ligne", fmtDur(ep.offline_after_s ?? EP_DEFAULTS.offline) + (ep.offline_after_s == null ? " (défaut)" : "")],
          ["Dernier contact (début)", fmtTimeTz(ep.started_at, tz)],
          ["Déclenchement", fmtTimeTz(ep.triggered_at, tz)],
          ["Durée", ep.status === "open" ? "en cours" : fmtDur(ep.duration_s)],
        ];
      default:
        return [];
    }
  };

  return (
    <div className="fixed inset-0 z-[3000] flex justify-end bg-black/50" onClick={onClose} data-testid="episode-drawer">
      <div className="bg-card w-full max-w-md h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border sticky top-0 bg-card">
          <div className="flex items-center gap-2">
            <Gauge className="w-5 h-5 text-[#17A2B8]" />
            <h3 className="font-bold">{EP_TYPES[ep.type] || ep.type}</h3>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold" style={{ color: sev.color, background: `${sev.color}22` }}>{sev.label}</span>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="text-muted-foreground hover:text-foreground p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-1">
          <Row label="Statut" value={ep.status === "open" ? "Ouvert" : "Fermé"} />
          <Row label="Véhicule" value={vehLabel(ep)} />
          {ep.message && <Row label="Message" value={ep.message} />}
          <Row label="Début réel" value={fmtTimeTz(ep.started_at, tz)} />
          {ep.triggered_at && ep.triggered_at !== ep.started_at && <Row label="Déclenchement" value={fmtTimeTz(ep.triggered_at, tz)} />}
          {ep.ended_at && <Row label="Fin" value={fmtTimeTz(ep.ended_at, tz)} />}
          <Row label="Durée" value={ep.status === "open" ? "en cours" : fmtDur(ep.duration_s)} />
          {ep.occurrence_count != null && <Row label="Occurrences" value={ep.occurrence_count} />}

          {typeRows().length > 0 && (
            <div className="pt-3 mt-2">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold mb-1">Détail {EP_TYPES[ep.type]}</p>
              {typeRows().map(([l, v]) => <Row key={l} label={l} value={v} />)}
            </div>
          )}

          {ep.type === "composee" && (
            <div className="pt-3 mt-2" data-testid="drawer-composite">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold mb-1">Règle composée</p>
              <Row label="Nom de la règle" value={ep.rule_name} />
              {ep.rule_version != null && <Row label="Version" value={`v${ep.rule_version}`} />}
              {ep.close_reason && <Row label="Motif de fermeture" value={CLOSE_REASONS[ep.close_reason] || ep.close_reason} />}
              {Array.isArray(ep.conditions_snapshot) && ep.conditions_snapshot.length > 0 && (
                <div className="mt-2 space-y-1.5" data-testid="drawer-composite-conditions">
                  {ep.conditions_snapshot.map((g, gi) => (
                    <div key={gi} className="text-xs border border-border/60 rounded-md p-2 bg-secondary/30">
                      {gi > 0 && <div className="text-[10px] font-bold text-[#F5821F] mb-1">ET</div>}
                      <span className="text-[10px] font-bold text-muted-foreground">{g.op === "OR" ? "AU MOINS UNE (OU)" : "TOUTES (ET)"}</span>
                      <ul className="mt-1 space-y-0.5">
                        {(g.conditions || []).map((c, ci) => <li key={ci}>• {condText(c)}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {ep.type !== "composee" && ep.close_reason && <Row label="Motif de fermeture" value={CLOSE_REASONS[ep.close_reason] || ep.close_reason} />}

          {(ep.lat != null || ep.last_lat != null) && (
            <div className="pt-3 mt-2">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold mb-1">Localisation</p>
              {ep.lat != null && <Row label="Lieu début" value={<AddressLabel lat={ep.lat} lon={ep.lon} testid="alert-ep-address-start" />} />}
              {ep.last_lat != null && <Row label={ep.status === "open" ? "Dernière position" : "Position fin"} value={<AddressLabel lat={ep.last_lat} lon={ep.last_lon} testid="alert-ep-address-end" />} />}
            </div>
          )}

          {isOffHoursEpisode(ep) && <OffHoursLinks ep={ep} />}

          <div className="pt-3 mt-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold mb-1">Acquittement</p>
            <Row label="Acquitté" value={ep.acknowledged ? "Oui" : "Non"} />
            {ep.acknowledged && <Row label="Date" value={fmtTimeTz(ep.acknowledged_at, tz)} />}
            {ep.acknowledged && <Row label="Par" value={ep.acknowledged_by_name} />}
          </div>

          {!ep.acknowledged && canAck && (
            <button onClick={onAck} data-testid="drawer-ack" className="mt-4 w-full flex items-center justify-center gap-2 bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold px-4 py-2.5 rounded-md text-sm">
              <Check className="w-4 h-4" /> Acquitter cet épisode
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
